import {
  CAlert,
  CButton,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CInputCheckbox,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CTextarea,
} from "@coreui/react";
import { useHistory } from "react-router-dom";
import {
  useCreateBucket,
  useDeleteBucket,
  useUpdateBucket,
} from "../buckets/BucketServices";
import { Can } from "src/helpers/Auth";
import CIcon from "@coreui/icons-react";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { DeleteItemModal } from "src/reusable/components/DeleteItemModal";

export const CreateBucketModal = (props) => {
  const [bucketName, setBucketName] = React.useState("");
  const [bucketDescription, setBucketDescription] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: createBucket,
    isLoading: isCreateBucketLoading,
    status: createBucketStatus,
    isError: isCreateBucketError,
    error: createBucketError,
    data: newBucket,
    reset: resetCreateBucket,
  } = useCreateBucket();

  useEffect(() => {
    if (createBucketStatus === "success" && newBucket) {
      props.onBucketCreate(newBucket);
    }
  }, [newBucket, createBucketStatus]);

  const resetAfterClose = () => {
    setBucketName("");
    setBucketDescription("");
    setErrorMessage(null);
    resetCreateBucket();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isCreateBucketError) {
    if (String(createBucketError?.response?.data?.detail) !== errorMessage) {
      setErrorMessage(String(createBucketError?.response?.data?.detail));
    }
  }
  const onSubmit = (name, desciption) => {
    if (name.length === 0) {
      setErrorMessage("Name cannot be empty");
    } else {
      setErrorMessage(null);
      let _newBucket = {
        name: name,
        description: desciption,
        analytic_fk: props.analyticId,
        selected_labels: props.selectedLabels,
      };
      let crossSection = "";
      let time = "";
      if (props.selectedCrossSection !== "") {
        crossSection = props.selectedCrossSection;
      }
      if (props.config.selectedCrossSection !== undefined) {
        crossSection = props.config.selectedCrossSection;
      }
      if (props.config.time !== undefined) {
        time = props.config.time;
      }
      _newBucket.config = {
        selected_cross_section: crossSection,
        time: time,
      };
      createBucket(_newBucket);
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <Can
        perform="bucket:write"
        yes={() => (
          <CModalHeader>
            <CCol sm="9">
              <h3>Create a new bucket</h3>
            </CCol>
            <CCol sm="3" style={{ textAlign: "right" }}>
              <CButton
                id="create-bucket-close-button"
                color="secondary"
                onClick={() => onClose()}
                size="sm"
              >
                <CIcon name="cil-x" />
              </CButton>
            </CCol>
          </CModalHeader>
        )}
        no={() => <div></div>}
      ></Can>
      <LoadingOverlay
        active={isCreateBucketLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating bucket..."
      >
        {createBucketStatus === "success" ? (
          <CModalBody>
            <span id="create-bucket-success-message">
              {newBucket.name} has been created!
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Name</CLabel>
                  <CCol sm={10}>
                    <CInput
                      id="create-new-bucket-name-input"
                      type="text"
                      name="name"
                      placeholder="Name"
                      value={bucketName}
                      onChange={(e) => setBucketName(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm={10}>
                    <CTextarea
                      id="create-new-bucket-description-textarea"
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={bucketDescription}
                      onChange={(e) => setBucketDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-create-new-bucket"
                  color="success"
                  onClick={() => onSubmit(bucketName, bucketDescription)}
                >
                  Create New Bucket
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateBucketModal.propTypes = {
  analyticId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  selectedLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCrossSection: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBucketCreate: PropTypes.func,
  config: PropTypes.object,
};

CreateBucketModal.defaultProps = {
  onBucketCreate: () => {}, //noop
};

export const UpdateBucketModal = (props) => {
  const [bucketName, setBucketName] = React.useState(props.bucketName);
  const [bucketDescription, setBucketDescription] = React.useState("");
  const [bucketAllowOthers, setBucketAllowOthers] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: updateBucket,
    isLoading: isUpdateBucketLoading,
    status: updateBucketStatus,
    isError: isUpdateBucketError,
    error: updateBucketError,
    reset: resetUpdateBucket,
  } = useUpdateBucket();

  useEffect(() => {
    setBucketDescription(props.bucketDescription);
    setBucketAllowOthers(props.bucketAllowOthersToEdit);
  }, [props]);

  const resetAfterClose = () => {
    setBucketDescription("");
    setBucketAllowOthers(false);
    setErrorMessage(null);
    resetUpdateBucket();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isUpdateBucketError) {
    if (String(updateBucketError.response.data.detail) !== errorMessage) {
      setErrorMessage(String(updateBucketError.response.data.detail));
    }
  }
  const onSubmit = (
    bucketId,
    _bucketName,
    description,
    allow_others_to_edit,
  ) => {
    setErrorMessage(null);
    updateBucket({
      bucketId,
      bucket: {
        name: _bucketName,
        config: props.config,
        description: description,
        allow_others_to_edit: allow_others_to_edit,
      },
    });
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>Update existing bucket</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="update-bucket-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isUpdateBucketLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Updating bucket..."
      >
        {updateBucketStatus === "success" ? (
          <CModalBody>
            <span id="update-bucket-success-message-span">Updated bucket</span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Name</CLabel>
                  <CCol sm="9">
                    <CInput
                      id="update-bucket-name-label"
                      type="text"
                      value={bucketName}
                      onChange={(e) => setBucketName(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm="9">
                    <CTextarea
                      id="update-bucket-description-textarea"
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={
                        bucketDescription === null ? "" : bucketDescription
                      }
                      onChange={(e) => setBucketDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel
                    htmlFor="allow_others_to_edit"
                    className="col-sm-3 col-form-label"
                  >
                    Allow others to edit
                  </CLabel>
                  <CCol sm="9">
                    <CInputCheckbox
                      id="update-bucket-allow-others-checkbox"
                      type="checkbox"
                      name="allow_others_to_edit"
                      style={{
                        marginTop: "10px",
                      }}
                      checked={!!bucketAllowOthers}
                      onChange={(e) => setBucketAllowOthers(e.target.checked)}
                    />
                  </CCol>
                </CFormGroup>
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="update-bucket-submit-button"
                  color="success"
                  onClick={() =>
                    onSubmit(
                      props.id,
                      bucketName,
                      bucketDescription,
                      bucketAllowOthers,
                    )
                  }
                >
                  Update Bucket
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

UpdateBucketModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  config: PropTypes.object,
  bucketName: PropTypes.string,
  bucketDescription: PropTypes.string,
  bucketAllowOthersToEdit: PropTypes.bool,
};

export const DeleteBucketModal = (props) => {
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: deleteBucket,
    isLoading: isDeleteBucketLoading,
    status: deleteBucketStatus,
    isError: isDeleteBucketError,
    error: deleteBucketError,
    reset: resetDeleteBucket,
    data: deleteBucketData,
  } = useDeleteBucket();

  const resetAfterClose = () => {
    setConfirmDelete("");
    setErrorMessage(null);
    resetDeleteBucket();
  };

  if (deleteBucketStatus === "success") {
    const history = useHistory();
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("bucketId");
    if (id === String(deleteBucketData)) {
      queryParams.delete("bucketId");
      history.replace({
        search: queryParams.toString(),
      });
      props.setBucketToNull(true);
    }
  }

  // If there was a error deleting the dataset, show message
  if (isDeleteBucketError) {
    if (String(deleteBucketError?.response?.data?.detail) !== errorMessage) {
      setErrorMessage(String(deleteBucketError.response.data.detail));
    }
  }
  const onSubmit = (bucketId) => {
    setErrorMessage(null);
    deleteBucket(bucketId);
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <DeleteItemModal
      isVisible={props.isVisible}
      onClose={onClose}
      itemName={props.bucketName}
      itemId={props.id}
      onSubmit={onSubmit}
      confirmDeleteString={confirmDelete}
      setConfirmDeleteString={setConfirmDelete}
      errorMessage={errorMessage}
      deleteItemStatus={deleteBucketStatus}
      isDeleteItemLoading={isDeleteBucketLoading}
      modalTitle="Delete Existing Bucket"
      deleteInstructions="CONFIRM YOU WANT TO DELETE THIS BUCKET BY TYPING THE BUCKET NAME HERE, THEN CLICK DELETE"
      buttonTitle="Delete Bucket"
    />
  );
};

DeleteBucketModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bucketName: PropTypes.string,
  id: PropTypes.number,
  setBucketToNull: PropTypes.func,
};
